import * as Phaser from 'phaser';
import { PaintedScene } from '../PaintedScene';
import {
  installSnowTexture,
  installBreathTexture,
  installSmokeTexture,
  createSnowfall,
  createBreathMist,
  SMOKE_TEXTURE_KEY,
} from './frostmereAmbient';

export const HEARTHMERE_HOLD_PHASER_KEY = 'HearthmereHoldScene' as const;
export const HEARTHMERE_HOLD_MANIFEST_KEY = 's-303-hearthmere-hold' as const;

const GLOW_TEXTURE_KEY = '__hearthmere_glow__';

const HEARTH_GLOW_NX = 0.47;
const HEARTH_GLOW_NY = 0.42;

const CHIMNEY_NX = 0.48;
const CHIMNEY_NY = 0.28;

const NPC_NX = 0.72;
const NPC_NY = 0.68;

export class HearthmereHoldScene extends PaintedScene {
  private snowEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private breathEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private smokeEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private hearthGlow: Phaser.GameObjects.Image | null = null;
  private glowTween: Phaser.Tweens.Tween | null = null;
  private npcSprite: Phaser.GameObjects.Rectangle | null = null;
  private npcLabel: Phaser.GameObjects.Text | null = null;

  constructor() {
    super(HEARTHMERE_HOLD_PHASER_KEY, HEARTHMERE_HOLD_MANIFEST_KEY);
  }

  preload(): void {
    super.preload();
    installSnowTexture(this);
    installBreathTexture(this);
    installSmokeTexture(this);
    this.installGlowTexture();
  }

  protected override onSceneReady(): void {
    this.buildHearthGlow();
    this.buildChimneySmoke();
    this.buildNpcPlaceholder();
    this.snowEmitter = createSnowfall(this, 1.0);

    if (this.walker) {
      this.breathEmitter = createBreathMist(this, this.walker);
    }
  }

  protected override onSceneShutdown(): void {
    this.snowEmitter?.destroy();
    this.breathEmitter?.destroy();
    this.smokeEmitter?.destroy();
    this.hearthGlow?.destroy();
    this.glowTween?.destroy();
    this.npcSprite?.destroy();
    this.npcLabel?.destroy();
    this.snowEmitter = null;
    this.breathEmitter = null;
    this.smokeEmitter = null;
    this.hearthGlow = null;
    this.glowTween = null;
    this.npcSprite = null;
    this.npcLabel = null;
  }

  private installGlowTexture(): void {
    if (this.textures.exists(GLOW_TEXTURE_KEY)) return;
    const SIZE = 64;
    const HALF = SIZE / 2;
    const gfx = this.make.graphics({ x: 0, y: 0 }, false);
    for (let r = HALF; r >= 1; r--) {
      const alpha = (1 - r / HALF) ** 3 * 0.7;
      gfx.fillStyle(0xffa040, alpha);
      gfx.fillCircle(HALF, HALF, r);
    }
    gfx.generateTexture(GLOW_TEXTURE_KEY, SIZE, SIZE);
    gfx.destroy();
  }

  private buildHearthGlow(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.hearthGlow = this.add.image(
      HEARTH_GLOW_NX * w,
      HEARTH_GLOW_NY * h,
      GLOW_TEXTURE_KEY,
    );
    this.hearthGlow.setOrigin(0.5, 0.5);
    this.hearthGlow.setScale(3.5);
    this.hearthGlow.setAlpha(0.35);
    this.hearthGlow.setDepth(4);
    this.hearthGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.hearthGlow.setScrollFactor(0);

    this.glowTween = this.tweens.add({
      targets: this.hearthGlow,
      alpha: { from: 0.3, to: 0.5 },
      scaleX: { from: 3.4, to: 3.7 },
      scaleY: { from: 3.4, to: 3.7 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private buildChimneySmoke(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.smokeEmitter = this.add.particles(CHIMNEY_NX * w, CHIMNEY_NY * h, SMOKE_TEXTURE_KEY, {
      speedX: { min: -6, max: 6 },
      speedY: { min: -20, max: -8 },
      alpha: { start: 0.3, end: 0 },
      scale: { start: 0.5, end: 2.0 },
      lifespan: { min: 3000, max: 5000 },
      frequency: 400,
      quantity: 1,
      maxAliveParticles: 15,
      blendMode: Phaser.BlendModes.NORMAL,
      advance: 3000,
    });
    this.smokeEmitter.setScrollFactor(0);
    this.smokeEmitter.setDepth(4);
  }

  private buildNpcPlaceholder(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const px = NPC_NX * w;
    const py = NPC_NY * h;

    this.npcSprite = this.add.rectangle(px, py, 24, 40, 0x8b6b4a, 0.8);
    this.npcSprite.setOrigin(0.5, 1);
    this.npcSprite.setDepth(100);

    this.npcLabel = this.add.text(px, py - 46, 'Firewood Chopper', {
      fontFamily: "'Almendra', serif",
      fontSize: '11px',
      color: '#eae0c8',
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 2, fill: true },
    });
    this.npcLabel.setOrigin(0.5, 1);
    this.npcLabel.setDepth(100);

    this.tweens.add({
      targets: this.npcSprite,
      scaleX: { from: 1.0, to: 0.95 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
}
